import React from "react";
import { InputNumber, Space, theme } from "antd";
import { useTranslation } from "react-i18next";
import UnitPartitionSelector from "./UnitPartitionSelector";
import { QuantityInputMethod } from "../../../models/order-product/types";

interface QuantityData {
  method: QuantityInputMethod;
  count?: number;
  unitPartitionId?: number;
  availableWeight?: number;
}

interface Product {
  id: number;
  unitPartition?: Array<{
    id: number;
    name: string;
    percentage: string | number;
  }>;
}

interface Props {
  value: QuantityData;
  onChange: (data: QuantityData) => void;
  disabled?: boolean;
  product?: Product; // Full product object with unitPartitions
}

const QuantityInputMethodComponent: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  product,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  // Check if product has unit partitions (for weight option availability)
  const hasUnitPartitions = product?.unitPartition && product.unitPartition.length > 0;

  const handleCountChange = (count: number | null) => {
    onChange({ ...value, count: count ?? undefined });
  };

  const handleUnitPartitionChange = (partitionId: number | null) => {
    onChange({ ...value, unitPartitionId: partitionId ?? undefined });
  };

  const handleWeightChange = (weight: number | null) => {
    onChange({ ...value, availableWeight: weight ?? undefined });
  };

  return (
    <div>
      {value.method === QuantityInputMethod.BY_COUNT ? (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ fontWeight: 600, fontSize: 14 }}>{t("count")}:</label>
            <InputNumber
              min={1}
              precision={0}
              step={1}
              value={value.count}
              onChange={handleCountChange}
              disabled={disabled}
              style={{ width: "100%", marginTop: 8 }}
              size="large"
              placeholder={t("enter_count")}
            />
          </div>
          {hasUnitPartitions && (
            <div>
              <label style={{ fontWeight: 600, fontSize: 14 }}>
                {t("unit_partition")} ({t("optional")}):
              </label>
              <div style={{ marginTop: 8 }}>
                <UnitPartitionSelector
                  value={value.unitPartitionId}
                  onChange={handleUnitPartitionChange}
                  disabled={disabled}
                  product={product}
                />
              </div>
            </div>
          )}
        </Space>
      ) : (
        <div>
          <label style={{ fontWeight: 600, fontSize: 14 }}>
            {t("available_weight")} ({t("units")}):
          </label>
          <InputNumber
            min={0.01}
            step={0.01}
            value={value.availableWeight}
            onChange={handleWeightChange}
            disabled={disabled}
            style={{ width: "100%", marginTop: 8 }}
            size="large"
            placeholder={t("enter_weight_in_units")}
          />
          <div style={{
            fontSize: 12,
            color: token.colorTextSecondary,
            marginTop: 6
          }}>
            {t("weight_hint_one_unit")}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuantityInputMethodComponent;
